import React, { useState } from "react";
import { User } from "../../../types";
import { ChevronRight } from "lucide-react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import Button from "../../ui/Button";
import SelectCard from "../SelectCard";
import StepBack from "../StepBack";

interface ActivityGoalStepProps {
  activityLevel?: User["activityLevel"];
  goal?: User["goal"];
  targetWeight?: number;
  weight?: number;
  onNext: (data: Partial<User>) => void;
  onBack: () => void;
}

const ACTIVITY_OPTIONS: { key: NonNullable<User["activityLevel"]>; icon: string }[] = [
  { key: "sedentary", icon: "🛋️" },
  { key: "light", icon: "🚶" },
  { key: "moderate", icon: "🏃" },
  { key: "active", icon: "🔥" },
];

const GOAL_OPTIONS: { key: NonNullable<User["goal"]>; icon: string }[] = [
  { key: "lose_weight", icon: "📉" },
  { key: "maintain", icon: "⚖️" },
  { key: "gain_muscle", icon: "💪" },
];

const ActivityGoalStep: React.FC<ActivityGoalStepProps> = ({
  activityLevel: initialActivity,
  goal: initialGoal,
  targetWeight: initialTargetWeight,
  weight,
  onNext,
  onBack,
}) => {
  const { t } = useTranslation();
  const [activityLevel, setActivityLevel] = useState<User["activityLevel"] | undefined>(
    initialActivity,
  );
  const [goal, setGoal] = useState<User["goal"] | undefined>(initialGoal);
  const [targetWeight, setTargetWeight] = useState<string>(
    initialTargetWeight?.toString() || "",
  );

  const parsedTargetWeight = targetWeight ? Number(targetWeight) : undefined;
  const targetWeightInvalid =
    Boolean(parsedTargetWeight) &&
    Boolean(weight) &&
    ((goal === "lose_weight" && parsedTargetWeight! >= weight!) ||
      (goal === "gain_muscle" && parsedTargetWeight! <= weight!));
  const isValid = Boolean(activityLevel && goal) && !targetWeightInvalid;

  const handleSubmit = () => {
    if (!isValid) return;
    onNext({
      activityLevel,
      goal,
      targetWeight: parsedTargetWeight,
    });
  };

  return (
    <div className="flex flex-col h-full px-4">
      <StepBack onBack={onBack} />

      <div className="flex-1 overflow-y-auto pb-24 scrollbar-hide">
        <div className="mb-4">
          <h2 className="text-xl font-black text-content-primary tracking-tight">
            {t("wizard.activity.title")}
          </h2>
          <p className="text-xs text-content-tertiary">{t("wizard.activity.subtitle")}</p>
        </div>

        <div className="grid grid-cols-1 gap-2 mb-6">
          {ACTIVITY_OPTIONS.map(({ key, icon }, index) => (
            <SelectCard
              key={key}
              selected={activityLevel === key}
              onClick={() => setActivityLevel(key)}
              icon={icon}
              title={t(`wizard.activity.${key}`)}
              description={t(`wizard.activity.${key}Desc`)}
              index={index}
            />
          ))}
        </div>

        <div className="mb-4">
          <h2 className="text-xl font-black text-content-primary tracking-tight">
            {t("wizard.goal.title")}
          </h2>
          <p className="text-xs text-content-tertiary">{t("wizard.goal.subtitle")}</p>
        </div>

        <div className="grid grid-cols-3 gap-2 mb-6">
          {GOAL_OPTIONS.map(({ key, icon }, index) => (
            <SelectCard
              key={key}
              compact
              selected={goal === key}
              onClick={() => setGoal(key)}
              icon={icon}
              title={t(`wizard.goal.${key}`)}
              index={index}
            />
          ))}
        </div>

        {goal && goal !== "maintain" && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}>
            <div className="bg-surface p-4 rounded-2xl border border-line shadow-card focus-within:ring-2 focus-within:ring-brand-500/30 focus-within:border-brand-400 transition-all">
              <label className="text-[10px] font-bold text-content-tertiary uppercase tracking-widest">
                {t("wizard.goal.targetWeight")}
              </label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="number"
                  inputMode="numeric"
                  value={targetWeight}
                  onChange={(e) => setTargetWeight(e.target.value)}
                  placeholder="65"
                  className="w-full text-lg font-bold text-content-primary outline-none bg-transparent placeholder:text-content-tertiary/40"
                />
                <span className="text-xs font-bold text-content-tertiary">
                  {t("wizard.bodyStats.weightSuffix")}
                </span>
              </div>
            </div>
            {targetWeightInvalid && (
              <p className="mt-2 text-xs font-medium text-warning">
                {goal === "lose_weight"
                  ? t("wizard.goal.targetMustBeLower")
                  : t("wizard.goal.targetMustBeHigher")}
              </p>
            )}
          </motion.div>
        )}
      </div>

      <div className="absolute bottom-0 left-0 w-full px-4 pt-4 pb-[calc(2rem+var(--safe-bottom))] bg-gradient-to-t from-app via-app to-transparent">
        <Button variant="primary" size="lg" fullWidth disabled={!isValid} onClick={handleSubmit}>
          {t("wizard.goal.viewPlan")} <ChevronRight size={18} />
        </Button>
      </div>
    </div>
  );
};

export default ActivityGoalStep;
